import url from "../config/axios";
import { encodeId } from "../utils/idObfuscator";

const normalizeAssetSource = (source) => {
  if (!source) return null;
  if (Array.isArray(source)) {
    const firstValid = source.flat(Infinity).find((item) => item);
    return normalizeAssetSource(firstValid);
  }
  if (typeof source === "object") {
    return (
      source.url || source.image || source.imageUrl || source.path || source.src || String(source)
    );
  }
  return String(source).trim();
};

export const getAsset = (filename) => {
  const server =
    process.env.REACT_APP_SERVER_URL ||
    (process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")
      : "") ||
    (typeof window !== "undefined" ? window.location.origin : "");

  const normalized = normalizeAssetSource(filename);
  if (!normalized) return `${server}/assets/`;

  const trimmed = normalized.trim();

  // If backend already returns absolute URL/data/blob, use it as-is.
  if (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }

  // ensure leading slash
  const withLeading = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

  // preserve absolute asset/image/upload paths from backend
  if (
    withLeading.startsWith("/assets/") ||
    withLeading.startsWith("/uploads/") ||
    withLeading.startsWith("/public/")
  ) {
    return `${server}${encodeURI(withLeading)}`;
  }

  // local server stores uploaded files under /assets/images,
  // but backend image field may contain /images/filename paths.
  if (withLeading.startsWith("/images/")) {
    return `${server}/assets${encodeURI(withLeading)}`;
  }

  // encodeURI to handle spaces and special characters while preserving slashes
  return `${server}/assets/images${encodeURI(withLeading)}`;
};

/** Supports string currency or object shapes ({ code, symbol, currencyName }). */
export const formatCurrencyCode = (currency, fallback = "$") => {
  if (currency == null || currency === "") return fallback;
  if (typeof currency === "string") {
    const trimmed = currency.trim();
    return trimmed ? trimmed.slice(0, 3) : fallback;
  }
  if (typeof currency === "object") {
    const label =
      currency.code ||
      currency.symbol ||
      currency.currencyName ||
      currency.currency ||
      "";
    const text = String(label).trim();
    return text ? text.slice(0, 3) : fallback;
  }
  return fallback;
};

export const getShopVisitUrl = (shop) => {
  if (!shop) return "/";
  const rawId = shop._id || shop.id || (typeof shop === "string" ? shop : "");
  const obfuscatedId = rawId ? encodeId(rawId) : "";
  const bt = shop.businessType || "";

  const visitRoutes = {
    homemadeShop: `/home-made-owner-profile/${obfuscatedId}`,
    farmingShop: `/farming-profile/${obfuscatedId}`,
    recycledShop: `/recycle-profile/${obfuscatedId}`,
    recycleShop: `/recycle-profile/${obfuscatedId}`,
    rawmaterialShop: `/rawmaterial-owner-profile/${obfuscatedId}`,
    manufacturerShop: `/manufacturer-owner-profile/${obfuscatedId}`,
    wholesellerShop: `/wholesaler-owner-profile/${obfuscatedId}`,
    wholesalerShop: `/wholesaler-owner-profile/${obfuscatedId}`,
    retailerShop: `/NewItem-bussines-Profile/${obfuscatedId}`,
    newItemShop: `/NewItem-bussines-Profile/${obfuscatedId}`,
    usedShop: `/Used-Items-Bussines-Profile/${obfuscatedId}`,
    usedItemShop: `/Used-Items-Bussines-Profile/${obfuscatedId}`,
    usedVehicleShop: `/used-vehicle-profile/${obfuscatedId}`,
    gemShop: `/minerals-and-mining-profile/${obfuscatedId}`,
    mineralShop: `/minerals-and-mining-profile/${obfuscatedId}`,
    foodShop: `/RestaurantAlldata/${obfuscatedId}`,
    restaurantShop: `/RestaurantAlldata/${obfuscatedId}`,
    businessPropertyCompany: `/Sale-Bussines-Property/${obfuscatedId}`,
    company: `/company-profile/${obfuscatedId}`,
    service: `/services-profile/${obfuscatedId}`,
    services: `/services-profile/${obfuscatedId}`,
    transportShop: `/transport-profile/${obfuscatedId}`,
    jobsCompany: `/find-jobs-online/${obfuscatedId}`,
  };

  return (
    visitRoutes[bt] ||
    (obfuscatedId ? `/home-made-owner-profile/${obfuscatedId}` : "/")
  );
};

